import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle, Building, User, Mail, Phone, TrendingUp } from 'lucide-react';
import { ThemeProvider } from 'styled-components';
import { theme } from '../../styles/theme';
import TextInput from '../TextInput.tsx';
import SearchableComboBox from '../SearchableComboBox.tsx';
import { BACKOFFICE_API_URL } from '../../config/api';

const LeadForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    whatsapp: '',
    monthlyRevenue: '',
    segment: '',
    selectedPlanId: new URLSearchParams(window.location.search).get('planId') || '',
    privacyPolicyAccepted: false,
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const formatWhatsApp = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let finalValue = type === 'checkbox' ? checked : value;
    
    if (name === 'whatsapp') {
      finalValue = formatWhatsApp(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const handleMonthlyRevenueChange = (value) => {
    setFormData(prev => ({
      ...prev,
      monthlyRevenue: value
    }));
  };

  const handleSegmentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      segment: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(`${BACKOFFICE_API_URL}/v1/backoffice/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          whatsapp: '',
          monthlyRevenue: '',
          segment: '',
          selectedPlanId: new URLSearchParams(window.location.search).get('planId') || '',
          privacyPolicyAccepted: false,
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(errorData.message || 'Ocorreu um erro ao enviar seus dados. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Lead submission error:', error);
      setStatus('error');
      setErrorMessage('Erro de conexão. Verifique sua internet e tente novamente.');
    }
  };

  if (status === 'success') {
    return (
      <section id="contato" className="py-5 bg-light-custom">
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center" data-aos="zoom-in">
              <div className="lead-form-card p-5">
                <CheckCircle size={80} className="text-success mb-4" />
                <h2 className="fw-bold mb-3">Recebemos seu contato!</h2>
                <p className="lead text-secondary mb-4">
                  Obrigado pelo interesse no Léo Delivery. Em breve um de nossos consultores entrará em contato com você para apresentar a melhor solução para seu negócio.
                </p>
                <button 
                  className="btn btn-primary-custom btn-lg px-5 py-3"
                  onClick={() => setStatus('idle')}
                >
                  Enviar outra mensagem
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <section id="contato" className="py-5 bg-light-custom">
        <div className="container py-5">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-5 mb-lg-0" data-aos="fade-right">
              <span className="badge bg-primary-custom text-primary-black mb-3 px-3 py-2 rounded-pill fw-bold">
                CONTATO
              </span>
              <h2 className="display-5 fw-bold mb-4">
                Pronto para transformar sua operação?
              </h2>
              <p className="lead text-secondary mb-5">
                Preencha o formulário e descubra como o Léo Delivery pode automatizar seu atendimento e aumentar seu lucro em poucos dias.
              </p>

              <div className="d-flex align-items-start mb-4">
                <div className="bg-white p-3 rounded-4 shadow-sm me-4">
                  <TrendingUp className="text-primary-custom" size={24} />
                </div>
                <div>
                  <h5 className="fw-bold mb-1">Crescimento acelerado</h5>
                  <p className="text-muted small mb-0">Clientes que utilizam nossa plataforma vendem em média 35% mais.</p>
                </div>
              </div>

              <div className="d-flex align-items-start">
                <div className="bg-white p-3 rounded-4 shadow-sm me-4">
                  <CheckCircle className="text-primary-custom" size={24} />
                </div>
                <div>
                  <h5 className="fw-bold mb-1">Implantação rápida</h5>
                  <p className="text-muted small mb-0">Seu cardápio online no ar em menos de 24 horas após o contato.</p>
                </div>
              </div>
            </div>

            <div className="col-lg-6" data-aos="fade-left">
              <div className="lead-form-card shadow-lg p-4 p-md-5">
                <h3 className="fw-bold mb-4">Fale com um consultor</h3>
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <TextInput
                        label="Nome completo"
                        name="name"
                        placeholder="Ex: João Silva"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        leftIcon={<User size={18} className="text-muted" />}
                      />
                    </div>
                    <div className="col-md-6 mb-4">
                      <TextInput
                        label="Nome da empresa"
                        name="company"
                        placeholder="Ex: Pizzaria do Bairro"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        leftIcon={<Building size={18} className="text-muted" />}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <TextInput
                        label="E-mail profissional"
                        type="email"
                        name="email"
                        placeholder="joao@empresa.com.br"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        leftIcon={<Mail size={18} className="text-muted" />}
                      />
                    </div>
                    <div className="col-md-6 mb-4">
                      <TextInput
                        label="WhatsApp"
                        type="tel"
                        name="whatsapp"
                        placeholder="(45) 99143-9499"
                        required
                        value={formData.whatsapp}
                        onChange={handleChange}
                        leftIcon={<Phone size={18} className="text-muted" />}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-4">
                      <SearchableComboBox
                        label="Faturamento mensal"
                        placeholder="Selecionar"
                        valueId={formData.monthlyRevenue}
                        onChange={handleMonthlyRevenueChange}
                        items={[
                          { id: 'DONT_KNOW', label: 'Não sei', sequence: 0 },
                          { id: 'NO_REVENUE', label: 'Não tem faturamento', sequence: 1 },
                          { id: 'LESS_THAN_1000', label: 'Menos de R$1000', sequence: 2 },
                          { id: 'ABOVE_100000', label: 'Acima de R$100.000', sequence: 3 },
                          { id: 'FROM_1001_TO_5000', label: 'R$1.001 à R$5.000', sequence: 4 },
                          { id: 'FROM_5001_TO_10000', label: 'R$5.001 à R$10.000', sequence: 5 },
                          { id: 'FROM_10001_TO_20000', label: 'R$10.001 à R$20.000', sequence: 6 },
                          { id: 'FROM_20001_TO_30000', label: 'R$20.001 à R$30.000', sequence: 7 },
                          { id: 'FROM_30001_TO_50000', label: 'R$30.001 à R$50.000', sequence: 8 },
                          { id: 'FROM_50010_TO_100000', label: 'R$50.001 à R$100.000', sequence: 9 }
                        ]}
                      />
                    </div>
                    <div className="col-md-6 mb-4">
                      <SearchableComboBox
                        label="Qual o seu segmento?"
                        placeholder="Selecionar"
                        valueId={formData.segment}
                        onChange={handleSegmentChange}
                        items={[
                          { id: 'PIZZARIA', label: 'Pizzaria', sequence: 0 },
                          { id: 'HAMBURGUERIA', label: 'Hamburgueria', sequence: 1 },
                          { id: 'RESTAURANTE', label: 'Restaurante', sequence: 2 },
                          { id: 'LANCHONETE', label: 'Lanchonete', sequence: 3 },
                          { id: 'CONFEITARIA', label: 'Confeitaria', sequence: 4 },
                          { id: 'ACAITERIA', label: 'Açaiteria', sequence: 5 },
                          { id: 'SUSHI', label: 'Sushi', sequence: 6 },
                          { id: 'MARMITARIA', label: 'Marmitaria', sequence: 7 },
                          { id: 'PASTELARIA', label: 'Pastelaria', sequence: 8 },
                          { id: 'OUTRO', label: 'Outro', sequence: 9 }
                        ]}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="privacyPolicyAccepted"
                        id="privacyPolicy"
                        required
                        checked={formData.privacyPolicyAccepted}
                        onChange={handleChange}
                      />
                      <label className="form-check-label small text-secondary" htmlFor="privacyPolicy">
                        Concordo com a <a href="/politica-de-privacidade.html" target="_blank" className="text-primary-custom">Política de Privacidade</a> e autorizo o contato.
                      </label>
                    </div>
                  </div>

                  {status === 'error' && (
                    <div className="alert alert-danger d-flex align-items-center mb-4 border-0 rounded-4">
                      <AlertCircle size={20} className="me-2" />
                      <span className="small">{errorMessage}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary-custom btn-lg w-100 py-3 rounded-4 fw-bold shadow-sm d-flex align-items-center justify-content-center"
                    disabled={status === 'loading'}
                  >
                    {status === 'loading' ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send size={18} className="me-2" />
                        Quero automatizar meu negócio
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ThemeProvider>
  );
};

export default LeadForm;
